import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, getDoc, setDoc, query, where, deleteDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged, createUserWithEmailAndPassword } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { FaTrash, FaEdit, FaToggleOn, FaToggleOff, FaPlus } from 'react-icons/fa';
import { sendEmail } from '../utils/email';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserDisplayName, setNewUserDisplayName] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newUserStatus, setNewUserStatus] = useState('enabled');
  const [formError, setFormError] = useState('');
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserDisplayName, setEditUserDisplayName] = useState('');
  const [editUserRole, setEditUserRole] = useState('user');
  const [editUserPassword, setEditUserPassword] = useState('');
  const [editConfirmPassword, setEditConfirmPassword] = useState('');
  const navigate = useNavigate();
  const auth = getAuth();

  // Fetch users
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/');
        return;
      }

      const userRef = doc(db, 'users', currentUser.uid);
      try {
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists() || userSnap.data()?.role !== 'admin') {
          navigate('/dashboard/overview');
          return;
        }

        const fetchUsers = async () => {
          setLoading(true);
          try {
            const usersRef = collection(db, 'users');
            const querySnapshot = await getDocs(usersRef);
            const usersData = querySnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
              predictionCounts: doc.data().predictionCounts ?? { xrayPneumonia: 0, xrayNormal: 0 },
            }));
            setUsers(usersData);
          } catch (err) {
            setError(`Failed to fetch users: ${err.message}`);
          } finally {
            setLoading(false);
          }
        };
        await fetchUsers();
      } catch (err) {
        setError(`Failed to verify admin status: ${err.message}`);
        navigate('/dashboard/overview');
      }
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  // Add new user
  const handleAddUser = async (e) => {
    e.preventDefault();
    setFormError('');

    if (newUserPassword !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, newUserEmail, newUserPassword);
      const user = userCredential.user;

      const newUserData = {
        email: newUserEmail,
        displayName: newUserDisplayName,
        username: newUserEmail.split('@')[0], // Add username as required by rules
        role: newUserRole,
        status: newUserStatus,
        credits: 0,
        plan: 'basic', // Add plan as required by rules
        predictionCounts: { xrayPneumonia: 0, xrayNormal: 0 },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await setDoc(doc(db, 'users', user.uid), newUserData);

      const emailBody = `Dear ${newUserDisplayName},\n\nYour account has been successfully created on Medpredix. You can log in using your email and the password you provided during registration.\n\nBest regards,\nMedpredix Team`;
      await sendEmail(newUserEmail, 'Welcome to Medpredix', emailBody);

      setUsers([...users, { id: user.uid, ...newUserData }]);
      setNewUserEmail('');
      setNewUserDisplayName('');
      setNewUserRole('user');
      setNewUserPassword('');
      setConfirmPassword('');
      setNewUserStatus('enabled');
      setShowAddForm(false);
    } catch (err) {
      console.error('Error creating user:', err);
      setFormError(`Error creating user: ${err.message}`);
    }
  };

  // Edit user
  const handleEditUser = async (e) => {
    e.preventDefault();
    setFormError('');

    if (editUserPassword && editUserPassword !== editConfirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    try {
      const userRef = doc(db, 'users', editUserId);
      const updateData = {
        email: editUserEmail,
        displayName: editUserDisplayName,
        role: editUserRole,
        updatedAt: new Date().toISOString(),
      };
      await updateDoc(userRef, updateData);

      setUsers(users.map((user) =>
        user.id === editUserId ? { ...user, ...updateData } : user
      ));
      setEditUserEmail('');
      setEditUserDisplayName('');
      setEditUserRole('user');
      setEditUserPassword('');
      setEditConfirmPassword('');
      setShowEditForm(false);
    } catch (err) {
      console.error('Error editing user:', err);
      setFormError(`Error editing user: ${err.message}`);
    }
  };

  // Toggle user status
  const toggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'enabled' ? 'disabled' : 'enabled';
    const userRef = doc(db, 'users', userId);

    try {
      const adminRef = doc(db, 'users', auth.currentUser.uid);
      const adminSnap = await getDoc(adminRef);
      if (!adminSnap.exists() || adminSnap.data().role !== 'admin') {
        throw new Error('You do not have permission to change user status.');
      }

      await updateDoc(userRef, { status: newStatus });
      setUsers(users.map((user) =>
        user.id === userId ? { ...user, status: newStatus } : user
      ));
    } catch (err) {
      console.error('Error toggling status:', err);
      setError(`Failed to toggle status: ${err.message}`);
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    const confirmed = confirmDelete(userId);
    if (!confirmed) return;

    try {
      // Check admin permissions
      const adminRef = doc(db, 'users', auth.currentUser.uid);
      const adminSnap = await getDoc(adminRef);
      if (!adminSnap.exists() || adminSnap.data().role !== 'admin') {
        throw new Error('You do not have permission to delete users.');
      }

      // Delete user document
      await deleteDoc(doc(db, 'users', userId));

      // Delete associated prediction history
      const historyQuery = query(collection(db, 'prediction_history'), where('userId', '==', userId));
      const historySnapshot = await getDocs(historyQuery);
      await Promise.all(historySnapshot.docs.map((doc) => deleteDoc(doc.ref)));

      // Update local state only after successful deletion
      setUsers(users.filter((user) => user.id !== userId));
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(`Failed to delete user: ${err.message}`);
    }
  };

  const confirmDelete = (userId) => {
    return window.confirm('Are you sure you want to delete this user? This will also delete their prediction history.');
  };

  // Update credits
  const updateCredits = async (userId, newCredits) => {
    try {
      await updateDoc(doc(db, 'users', userId), { credits: parseInt(newCredits) || 0 });
      setUsers(users.map((user) =>
        user.id === userId ? { ...user, credits: parseInt(newCredits) || 0 } : user
      ));
    } catch (err) {
      setError('Failed to update credits: ' + err.message);
    }
  };

  const handleEditClick = (userId) => {
    const user = users.find((user) => user.id === userId);
    setEditUserId(userId);
    setEditUserEmail(user.email);
    setEditUserDisplayName(user.displayName);
    setEditUserRole(user.role);
    setShowEditForm(true);
  };

  if (loading) return <div className="p-6 text-gray-600">Loading...</div>;

  return (
    <div className="p-6 font-sans">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-4xl font-bold text-gray-700">Users</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center"
        >
          <FaPlus className="mr-2" /> Add New User
        </button>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}

      {showAddForm && (
        <div className="mb-6 p-4 bg-white border rounded-lg shadow-sm">
          <form onSubmit={handleAddUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  className="mt-1 block w-full rounded border-gray-300 shadow-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Display Name</label>
                <input
                  type="text"
                  value={newUserDisplayName}
                  onChange={(e) => setNewUserDisplayName(e.target.value)}
                  className="mt-1 block w-full rounded border-gray-300 shadow-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <input
                  type="password"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  className="mt-1 block w-full rounded border-gray-300 shadow-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full rounded border-gray-300 shadow-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value)}
                  className="mt-1 block w-full rounded border-gray-300 shadow-sm"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={newUserStatus}
                  onChange={(e) => setNewUserStatus(e.target.value)}
                  className="mt-1 block w-full rounded border-gray-300 shadow-sm"
                  required
                >
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
            </div>

            {formError && <p className="text-red-500 text-sm">{formError}</p>}

            <div className="flex space-x-2">
              <button
                type="submit"
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              >
                Create User
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {showEditForm && (
        <div className="mb-6 p-4 bg-white border rounded-lg shadow-sm">
          <form onSubmit={handleEditUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={editUserEmail}
                  onChange={(e) => setEditUserEmail(e.target.value)}
                  className="mt-1 block w-full rounded border-gray-300 shadow-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Display Name</label>
                <input
                  type="text"
                  value={editUserDisplayName}
                  onChange={(e) => setEditUserDisplayName(e.target.value)}
                  className="mt-1 block w-full rounded border-gray-300 shadow-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Password (optional)</label>
                <input
                  type="password"
                  value={editUserPassword}
                  onChange={(e) => setEditUserPassword(e.target.value)}
                  className="mt-1 block w-full rounded border-gray-300 shadow-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                <input
                  type="password"
                  value={editConfirmPassword}
                  onChange={(e) => setEditConfirmPassword(e.target.value)}
                  className="mt-1 block w-full rounded border-gray-300 shadow-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={editUserRole}
                  onChange={(e) => setEditUserRole(e.target.value)}
                  className="mt-1 block w-full rounded border-gray-300 shadow-sm"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            {formError && <p className="text-red-500 text-sm">{formError}</p>}

            <div className="flex space-x-2">
              <button
                type="submit"
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              >
                Update User
              </button>
              <button
                type="button"
                onClick={() => setShowEditForm(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {users.length === 0 ? (
        <p className="text-gray-600">No users found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-300 text-sm">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="py-3 px-4 border-b">Name</th>
                <th className="py-3 px-4 border-b">Email</th>
                <th className="py-3 px-4 border-b">Credits</th>
                <th className="py-3 px-4 border-b">Status</th>
                <th className="py-3 px-4 border-b">X-ray Pneumonia</th>
                <th className="py-3 px-4 border-b">X-ray Normal</th>
                <th className="py-3 px-4 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4">{user.displayName || 'N/A'}</td>
                  <td className="py-3 px-4">{user.email}</td>
                  <td className="py-3 px-4">
                    <input
                      type="number"
                      defaultValue={user.credits}
                      onBlur={(e) => updateCredits(user.id, e.target.value)}
                      className="w-16 p-1 border rounded"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <button onClick={() => toggleStatus(user.id, user.status)} className="text-lg">
                      {user.status === 'enabled' ? <FaToggleOn className="text-green-500" /> : <FaToggleOff className="text-gray-500" />}
                    </button>
                  </td>
                  <td className="py-3 px-4">{user.predictionCounts.xrayPneumonia ?? 0}</td>
                  <td className="py-3 px-4">{user.predictionCounts.xrayNormal ?? 0}</td>
                  <td className="py-3 px-4 flex space-x-2">
                    <button onClick={() => handleEditClick(user.id)} className="text-yellow-600 hover:text-yellow-800">
                      <FaEdit size={18} />
                    </button>
                    <button onClick={() => deleteUser(user.id)} className="text-red-600 hover:text-red-800">
                      <FaTrash size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Users;